import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab'
import { Box, Chip, Paper, Typography } from '@mui/material'
import type { Maybe, PaymentHistoryItem, PaymentStatus } from '../generated/graphql'
import { formatStatus, formatTimestamp } from '../utils/util'

const STATUS_COLOR: Partial<Record<PaymentStatus, 'error' | 'success' | 'warning' | 'info' | 'grey'>> = {
  FAILED: 'error',
  CANCELLED: 'error',
  COMPLETED: 'success',
  PENDING_BANK_INFO: 'warning',
  APPROVED: 'info',
  ENQUEUED_FOR_RELEASE: 'info',
  SUBMITTED: 'info',
  PROCESSING: 'info',
  OPEN: 'grey',
  UNDER_REVIEW: 'grey',
  HELD: 'grey',
}

function getStatusColor(status?: Maybe<PaymentStatus>) {
  return status ? (STATUS_COLOR[status] ?? 'grey') : 'grey'
}

interface PaymentHistoryTabProps {
  history?: Array<PaymentHistoryItem | null> | null
}

export default function PaymentHistoryTab({ history }: PaymentHistoryTabProps) {
  const sortedHistory = [...(history || [])]
    .filter((item): item is PaymentHistoryItem => item !== null)
    .sort((a, b) => {
      const tsA = a.timestamp ? new Date(a.timestamp).getTime() : 0
      const tsB = b.timestamp ? new Date(b.timestamp).getTime() : 0
      return tsB - tsA
    })

  if (sortedHistory.length === 0) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No history available
        </Typography>
      </Box>
    )
  }

  return (
    <Timeline position="right" sx={{ py: 0, '& .MuiTimelineItem-root:before': { flex: 0, padding: 0 } }}>
      {sortedHistory.map((item, index) => {
        const key = item.timestamp ? `${item.timestamp}-${item.status ?? ''}-${index}` : `item-${index}`
        const hasError =
          item.isBankError === true ||
          !!item.paymentErrorMessage ||
          !!(item.failureDetail && (item.failureDetail.type || item.failureDetail.description))
        const hasDetails = !!(item.paymentOption || item.payToPaymentMethodId)
        const hasCards = hasDetails || hasError
        const dotColor = hasError ? 'error' : getStatusColor(item.status ?? undefined)
        const isLast = index === sortedHistory.length - 1

        return (
          <TimelineItem key={key}>
            <TimelineSeparator>
              <TimelineDot color={dotColor} />
              {!isLast && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent sx={{ pb: 1, px: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1,
                  mb: hasCards ? 1.5 : 0,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {formatTimestamp(item.timestamp)}
                </Typography>
                <Chip
                  label={formatStatus(item.status)}
                  size="small"
                  color={dotColor === 'grey' ? 'default' : dotColor}
                  variant="outlined"
                />
                {item.isBankError === true && <Chip label="Bank error" color="error" size="small" />}
              </Box>

              {hasCards && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}
                >
                  {item.paymentOption && (
                    <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Payment option
                      </Typography>
                      <Typography variant="body2">{item.paymentOption.replace(/_/g, ' ')}</Typography>
                    </Box>
                  )}
                  {item.payToPaymentMethodId && (
                    <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Payment method ID
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {item.payToPaymentMethodId}
                      </Typography>
                    </Box>
                  )}
                  {item.paymentErrorMessage && !item.failureDetail?.description && (
                    <Box sx={{ flexBasis: '100%' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Error message
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {item.paymentErrorMessage}
                      </Typography>
                    </Box>
                  )}
                  {item.failureDetail?.type && (
                    <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Failure type
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {item.failureDetail.type}
                      </Typography>
                    </Box>
                  )}
                  {item.failureDetail?.description && (
                    <Box sx={{ flexBasis: '100%' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Failure description
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {item.failureDetail.description}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              )}
            </TimelineContent>
          </TimelineItem>
        )
      })}
    </Timeline>
  )
}
